/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// MUI
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme, useMediaQuery } from "@mui/material";

// MUI Icons
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

// Open Layers
import OlFormatWKT from "ol/format/WKT";
import OlPoint from "ol/geom/Point";

// Icons
import DomIcon from "@/icons/search/DomIcon";
import LokacijaIcon from "@/icons/search/LokacijaIcon";
import NaseljeIcon from "@/icons/search/NaseljeIcon";
import ParkIcon from "@/icons/search/ParkIcon";
import RijekaIcon from "@/icons/search/RijekaIcon";
import PenjalisteIcon from "@/icons/search/PenjalisteIcon";
import PlaninaIcon from "@/icons/search/PlaninaIcon";
import PolazisteIcon from "@/icons/search/PolazisteIcon";
import OdmoristeIcon from "@/icons/search/OdmoristeIcon";
import SklonisteIcon from "@/icons/search/SklonisteIcon";
import VrhIcon from "@/icons/search/VrhIcon";

import SlapIcon from "@/icons/search/SlapIcon";
import IzvorIcon from "@/icons/search/IzvorIcon";
import SpiljaIcon from "@/icons/search/SpiljaIcon";

import ArheoIcon from "@/icons/search/ArheoIcon";
import MlinIcon from "@/icons/search/MlinIcon";
import MuzejIcon from "@/icons/search/MuzejIcon";
import SakralniIcon from "@/icons/search/SakralniIcon";
import SpomenikIcon from "@/icons/search/SpomenikIcon";
import UtvrdaIcon from "@/icons/search/UtvrdaIcon";
// import { ScatterPlot as ScatterPlotIcon } from "@mui/icons-material";

// Custom Components
import MapContext from "@/components/Map/MapContext";
import dataController from "@/lib/dataController";
import modelToponimi from "@/models/toponimi";
import SearchPaneItems from "./SearchPaneItems";

// Types
import { DCRecord } from "@/@types/lib/dataController";
import { sifrarnici } from "@/lib/sifrarnici";

type SearchPaneType =
  {
    onExplore?: (id: number) => void;
    onAddToRoute?: (coord: number[] | undefined, label: string, poi_id?: number) => void;
    readOnly?: boolean
    // handleOpenObilaznica: (obilaznicaId: number, poi_id: number) => void;
  }

function SearchPane(props: SearchPaneType) {
  const { t } = useTranslation();

  const { onExplore, onAddToRoute, readOnly = false } = props;

  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

  const mapContext = useContext(MapContext);

  const [value, setValue] = useState<string>("");
  const [records, setRecords] = useState<DCRecord[]>([]);
  const [results, setResults] = useState<DCRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const textBox = useRef<HTMLInputElement>(null);

  const theme = useTheme();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    reloadToponimi();
  }, [])

  useEffect(() => {
    if (value.length >= 3) {
      const filtered = filterResults(value, records);
      setResults(filtered);
    } else {
      setResults([]);
    }

  }, [value])

  const reloadToponimi = () => {
    const dc = new dataController(modelToponimi);

    setLoading(true);
    dc.GetData().then((resp) => {
      if (resp.success) {
        if (Array.isArray(resp.data)) {
          setRecords(resp.data || []);
        }
        else {
          setRecords([]);
        }
      };
    })
      .finally(() => {
        setLoading(false);
      })
  }

  const deaccent = (value: string) => {
    let r = value.toLowerCase();
    r = r.replace(new RegExp(/[čć]/g), "c");
    r = r.replace(new RegExp(/[ž]/g), "z");
    r = r.replace(new RegExp(/[š]/g), "s");
    r = r.replace(new RegExp(/[đ]/g), "d");

    return r;
  }

  const filterResults = (value: string, records: DCRecord[]) => {
    const value_deacc = deaccent(value as string);
    const res = records.filter(x => {
      const naziv = x["naziv"] as string;
      const naziv_alt = x["naziv_alt"];
      const found = false;
      if (naziv) {
        const found = deaccent(naziv).indexOf(value_deacc) >= 0;
        if (found) {
          return true;
        } else if (naziv_alt) {
          return deaccent(naziv_alt as string).indexOf(value_deacc) >= 0;
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
    return res;
  };

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const val = evt.target.value;
    setValue(val);
  };

  const handleClick = (evt: React.MouseEvent<HTMLDivElement>, item: DCRecord) => {
    zoomTo(item);
    const id = item["id"] as number;
    setSelectedId(id);
  };

  const handleAddToRoute = (evt: React.MouseEvent<HTMLButtonElement>, item: DCRecord) => {
    const wkt = item["wkt"];
    // const tip = item["toponim_tip_id"];
    if (wkt) {
      const wktFormatter = new OlFormatWKT();
      const feat = wktFormatter.readFeature(wkt, {
        dataProjection: "EPSG:3765",
        featureProjection: "EPSG:3857"
      })

      if (onAddToRoute) {
        const geom = feat.getGeometry();
        if (geom?.getType() === "Point") {
          const coords = (geom as OlPoint).getCoordinates();
          const naziv = item["naziv"] as string;
          const poi_id = item["id"] as number;
          onAddToRoute(coords, naziv, poi_id ?? undefined);
        }
      }
    }
  };

  const handleEnterPress = () => {
    if (results.length === 1) {
      zoomTo(results[0])
      const id = results[0]["id"] as number;
      setSelectedId(id);
    }
  };

  const handleClear = () => {
    setValue('');
    setTimeout(() => {
      textBox.current?.focus();
    }, 100);
  };

  const zoomTo = (item: DCRecord) => {
    const wkt = item["wkt"];
    const tip = item["toponim_tip_id"];
    if (wkt) {
      const wktFormatter = new OlFormatWKT();
      const feat = wktFormatter.readFeature(wkt, {
        dataProjection: "EPSG:3765",
        featureProjection: "EPSG:3857"
      })

      if (onExplore && (
        tip === sifrarnici.toponim_tipovi.plan_kuca_dom
        || tip === sifrarnici.toponim_tipovi.plan_skloniste
        || tip === sifrarnici.toponim_tipovi.penjaliste
        || tip === sifrarnici.toponim_tipovi.vrh
        || tip === sifrarnici.toponim_tipovi.polaziste
      )) {
        const id = item["id"] as number;
        if (id) {
          onExplore(id);
        }
      }

      if (mapContext && mapContext.map) {
        const view = mapContext.map.getView();
        const extent = feat.getGeometry()?.getExtent();

        const padding = isMobile ? [20, 20, 300, 20] : [20, 20, 20, 20]
        if (extent)
          view.fit(extent, { padding: padding, duration: 500, maxZoom: 15 });
      }
    }
  };

  const toponim_tip_field = "toponim_tip_id";

  const planine = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.planina);
  const objekti = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.plan_kuca_dom);
  const sklonista = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.plan_skloniste);
  const penjalista = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.penjaliste);
  const lokacije = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.lokacija);
  const naselja = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.naselje);
  const vrhovi = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.vrh);
  const polazista = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.polaziste);
  const odmorista = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.odmoriste);
  const parkovi = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.park);
  const rijeke = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.rijeka);
  // const obilaznice = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.obilaznica);

  const izvori = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.izvor);
  const slapovi = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.slap);
  const spilje = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.spilja);

  const arheo_nalazista = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.arheo);
  const mlinovi = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.mlin);
  const muzeji = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.muzej);
  const sakralni_objekti = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.sakralni);
  const spomenici = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.spomenik);
  const utvrde = results.filter(x => x[toponim_tip_field] === sifrarnici.toponim_tipovi.utvrda);

  const textFnNull = (item: DCRecord) => null;

  const textFnNaziv = (item: DCRecord) => {
    const naziv = item["naziv"] as string;
    const naziv_ttoken = item["naziv_ttoken"]
    return naziv_ttoken ? t(naziv_ttoken as string) as string
      : naziv ? naziv
        : null;
  }

  const textFnOpis = (item: DCRecord) => {
    const opis = item["opis"] as string;
    const opis_ttoken = item["opis_ttoken"];

    return opis_ttoken ? t(opis_ttoken as string) as string
      : opis ? opis
        : null;
  }

  const textFnOpisVisina = (item: DCRecord) => {
    const opis = item["opis"] as string;
    const opis_ttoken = item["opis_ttoken"];
    const visina = item["visina_m"];

    let str = opis_ttoken ? t(opis_ttoken as string) as string : opis;
    str = str ? `${str}, ${visina} m` : `${visina} m`

    return str;
  }

  const searcPaneItemsCommonProps = {
    onClick: handleClick,
    onAddToRoute: handleAddToRoute,
    selectedId
  }

  // todo modify extent if its on mobile?

  return (
    <Box height="100%">
      <Box p={0}>
        <TextField
          inputRef={textBox}
          value={value}
          onChange={handleChange}
          disabled={records.length === 0}
          fullWidth
          placeholder={t("common.planine") + ", " + t("common.plan_objekti") + ", " + t("common.vrhovi") + "..."}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? <CircularProgress size={20} /> : <SearchIcon />}
              </InputAdornment>
            ),
            endAdornment: value.length > 0 ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClear}
                  edge="end"
                  tabIndex={-1}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }}
          onKeyDown={(evt) => {
            if (evt.key === "Enter") {
              handleEnterPress();
            } else if (evt.key === "Escape") {
              handleClear();
            }
          }}
        />
      </Box>
      {results ?
        <List sx={{
          width: "100%",
          bgcolor: "background.paper",
          position: "relative",
          overflow: "auto",
          height: "calc( 100vh -  255px )",
          "& ul": { padding: 0 },
          transition: "all 0.3s"
        }}
        >
          {planine.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={planine}
              itemIcon={<PlaninaIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnNull}
              canAddToRoute={false}
            /> : null}
          {sklonista.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={sklonista}
              itemIcon={<SklonisteIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpisVisina}
              canAddToRoute={readOnly ? false : true}
            /> : null}
          {objekti.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={objekti}
              itemIcon={<DomIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpisVisina}
              canAddToRoute={readOnly ? false : true}
            /> : null}
          {vrhovi.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={vrhovi}
              itemIcon={<VrhIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpisVisina}
              canAddToRoute={readOnly ? false : true}
            /> : null}
          {penjalista.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={penjalista}
              itemIcon={<PenjalisteIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpisVisina}
              canAddToRoute={readOnly ? false : true}
            /> : null}
          {polazista.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={polazista}
              itemIcon={<PolazisteIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}
          {odmorista.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={odmorista}
              itemIcon={<OdmoristeIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}
          {lokacije.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={lokacije}
              itemIcon={<LokacijaIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={false}
            /> : null}
          {naselja.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={naselja}
              itemIcon={<NaseljeIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={false}
            /> : null}
          {parkovi.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={parkovi}
              itemIcon={<ParkIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={false}
            /> : null}
          {rijeke.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={rijeke}
              itemIcon={<RijekaIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={false}
            /> : null}

          {slapovi.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={slapovi}
              itemIcon={<SlapIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}
          {izvori.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={izvori}
              itemIcon={<IzvorIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}
          {spilje.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={spilje}
              itemIcon={<SpiljaIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}

          {arheo_nalazista.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={arheo_nalazista}
              itemIcon={<ArheoIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}

          {mlinovi.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={mlinovi}
              itemIcon={<MlinIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}

          {muzeji.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={muzeji}
              itemIcon={<MuzejIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}

          {sakralni_objekti.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={sakralni_objekti}
              itemIcon={<SakralniIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}

          {spomenici.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={spomenici}
              itemIcon={<SpomenikIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}

          {utvrde.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={utvrde}
              itemIcon={<UtvrdaIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={readOnly ? false : true}
            /> : null}

          {/* {obilaznice.length > 0 ?
            <SearchPaneItems
              {...searcPaneItemsCommonProps}
              items={obilaznice}
              itemIcon={<ScatterPlotIcon color='secondary' />}
              primaryTextFn={textFnNaziv}
              secondaryTextFn={textFnOpis}
              canAddToRoute={false}
              handleOpenObilaznica={handleOpenObilaznica}
            /> : null} */}
        </List>
        : null}
    </Box>
  );
}

export default SearchPane;